import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { buildSurveyWorkbook, downloadSurveyWorkbook } from '../utils/surveyWorkbook';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig as ShadcnChartConfig,
} from '@/components/ui/chart';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  IconDownload,
  IconFileTypePdf,
  IconFileSpreadsheet,
  IconLoader2,
} from '@tabler/icons-react';
import { formatDateTime, formatDate as formatDateUtil, formatChartDate as formatChartDateUtil } from '../utils/dateFormat';
import { useSurveyData, SurveyResponse } from './surveys/hooks/useSurveyData';
import { extractQuestions } from './surveys/utils/questionUtils';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';

function parseOverallTimeMs(
  d: { date: string },
  timeGrouping: 'day' | 'week' | 'month' | 'year',
): number {
  if (timeGrouping === 'day') {
    const [year, month, day] = d.date.split('-');
    return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10)).getTime();
  }
  if (timeGrouping === 'week') {
    const match = d.date.match(/(\d{4})-W(\d{2})/);
    if (match) {
      const year = parseInt(match[1], 10);
      const week = parseInt(match[2], 10);
      const date = new Date(year, 0, 1);
      const dayOfWeek = date.getDay();
      const daysToAdd = (week - 1) * 7 - dayOfWeek;
      date.setDate(date.getDate() + daysToAdd);
      return date.getTime();
    }
    return new Date(d.date).getTime();
  }
  if (timeGrouping === 'month') {
    const [year, month] = d.date.split('-');
    return new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1).getTime();
  }
  return new Date(parseInt(d.date, 10), 0, 1).getTime();
}

function formatTimeSeriesDateLabel(
  d: { dateKey: string },
  timeGrouping: 'day' | 'week' | 'month' | 'year',
  formatChartDateFn: typeof formatChartDateUtil,
): string {
  if (timeGrouping === 'day') {
    const [year, month, day] = d.dateKey.split('-');
    const date = new Date(Date.UTC(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10)));
    return formatChartDateFn(date, 'day');
  }
  if (timeGrouping === 'week') {
    const match = d.dateKey.match(/(\d{4})-W(\d{2})/);
    if (match) {
      return `W${match[2]} ${match[1]}`;
    }
    return d.dateKey;
  }
  if (timeGrouping === 'month') {
    const [year, month] = d.dateKey.split('-');
    const date = new Date(Date.UTC(parseInt(year, 10), parseInt(month, 10) - 1, 1));
    return formatChartDateFn(date, 'month');
  }
  return d.dateKey;
}

export function SurveyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { survey, responses, loading, error, timeGrouping, setTimeGrouping } = useSurveyData(id);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [previewType, setPreviewType] = useState<'excel' | 'pdf' | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [expandedTextfieldQuestions, setExpandedTextfieldQuestions] = useState<Set<string>>(new Set());

  const formatChartDate = formatChartDateUtil;
  const overallChartRef = useRef<HTMLDivElement>(null);
  const overallChartPDFRef = useRef<HTMLDivElement>(null);
  const questionChartRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const questionChartPDFRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const questionBarChartRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const questionPieChartRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const sanitizeFileName = (name: string | null | undefined, fallback: string = 'survey'): string => {
    if (!name) return fallback;
    return name.replace(/[^a-z0-9]/gi, '_') || fallback;
  };

  const questions = useMemo(() => {
    return extractQuestions(survey);
  }, [survey]);

  const statistics = useMemo(() => {
    if (responses.length === 0) {
      return null;
    }

    const responsesToProcess = responses;

    const getSubmissionKey = (response: SurveyResponse): string => {
      const customerId = response.i_customer || response.user_id || 'anonymous';
      if (response.created_at) {
        try {
          const date = new Date(response.created_at);
          if (!isNaN(date.getTime())) {
            const roundedDate = new Date(date);
            roundedDate.setSeconds(0, 0);
            return `${customerId}_${roundedDate.getTime()}`;
          }
        } catch (e) {
          console.warn('Invalid date in survey response (getSubmissionKey):', response.created_at, e);
        }
      }
      return `${customerId}_${response.created_at || 'unknown'}`;
    };

    const uniqueSubmissions = new Set<string>();
    const submissionDates = new Map<string, Date>();
    
    responsesToProcess.forEach((response) => {
      const submissionKey = getSubmissionKey(response);
      uniqueSubmissions.add(submissionKey);
      
      if (response.created_at) {
        try {
          const date = new Date(response.created_at);
          if (!isNaN(date.getTime())) {
            const existingDate = submissionDates.get(submissionKey);
            if (!existingDate || date.getTime() < existingDate.getTime()) {
              submissionDates.set(submissionKey, date);
            }
          }
        } catch (e) {
          console.warn('Invalid date in survey response (submissionDates):', response.created_at, e);
        }
      }
    });

    const uniqueSubmissionCount = uniqueSubmissions.size;

    const submissionDatesArray = Array.from(submissionDates.values());
    let firstResponseDate: Date | null = null;
    let lastResponseDate: Date | null = null;

    if (submissionDatesArray.length > 0) {
      submissionDatesArray.sort((a, b) => a.getTime() - b.getTime());
      firstResponseDate = submissionDatesArray[0];
      lastResponseDate = submissionDatesArray[submissionDatesArray.length - 1];
    }

    const getDayKey = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const getWeekKey = (date: Date): string => {
      const year = date.getFullYear();
      const startOfYear = new Date(year, 0, 1);
      const days = Math.floor((date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
      const weekNumber = Math.ceil((days + startOfYear.getDay() + 1) / 7);
      return `${year}-W${String(weekNumber).padStart(2, '0')}`;
    };

    const getMonthKey = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${year}-${month}`;
    };

    const getYearKey = (date: Date): string => {
      return String(date.getFullYear());
    };

    const getKeyFunction = (grouping: 'day' | 'week' | 'month' | 'year') => {
      switch (grouping) {
        case 'day': return getDayKey;
        case 'week': return getWeekKey;
        case 'month': return getMonthKey;
        case 'year': return getYearKey;
        default: return getDayKey;
      }
    };

    const getKey = getKeyFunction(timeGrouping);
    const overallTimeSeries: { [date: string]: number } = {};
    
    submissionDates.forEach((date) => {
      const key = getKey(date);
      overallTimeSeries[key] = (overallTimeSeries[key] || 0) + 1;
    });

    const overallTimeSeriesArray = Object.entries(overallTimeSeries)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));

    const getDefaultViewRange = () => {
      if (overallTimeSeriesArray.length === 0) return { start: 0, end: 0 };
      const totalItems = overallTimeSeriesArray.length;
      let itemsToShow: number;
      switch (timeGrouping) {
        case 'day': itemsToShow = Math.min(30, totalItems); break;
        case 'week': itemsToShow = Math.min(12, totalItems); break;
        case 'month': itemsToShow = Math.min(12, totalItems); break;
        case 'year': itemsToShow = totalItems; break;
        default: itemsToShow = Math.min(30, totalItems);
      }
      return { start: Math.max(0, totalItems - itemsToShow), end: totalItems };
    };

    const defaultViewRange = getDefaultViewRange();

    const stats: any = {
      totalResponses: uniqueSubmissionCount,
      firstResponse: firstResponseDate,
      lastResponse: lastResponseDate,
      dateRange: null,
      questionStats: [],
      overallTimeSeries: overallTimeSeriesArray,
      overallTimeSeriesDefaultView: defaultViewRange,
      timeGrouping: timeGrouping,
    };

    if (firstResponseDate && lastResponseDate) {
      stats.dateRange = `${formatChartDate(firstResponseDate, 'day')} - ${formatChartDate(lastResponseDate, 'day')}`;
    }

    const responsesByQuestion: { [key: string]: SurveyResponse[] } = {};
    responsesToProcess.forEach((response) => {
      const qNum = response.question_number;
      if (qNum !== null && qNum !== undefined) {
        const key = String(qNum);
        if (!responsesByQuestion[key]) {
          responsesByQuestion[key] = [];
        }
        responsesByQuestion[key].push(response);
      }
    });

    questions.forEach((question) => {
      const questionResponses = responsesByQuestion[String(question.number)] || 
                                responsesByQuestion[question.number.toString()] || 
                                [];

      const responseCounts: { [value: string]: number } = {};
      let totalAnswers = 0;

      questionResponses.forEach((response) => {
        const userResponse = response.user_response;
        if (userResponse !== null && userResponse !== undefined) {
          totalAnswers++;
          const valueStr = String(userResponse);
          responseCounts[valueStr] = (responseCounts[valueStr] || 0) + 1;
        }
      });

      let answerBreakdown: Array<{ value: string; count: number; percentage: string; label?: string }> = [];
      
      if (question.type === 'rating') {
        let maxRating = 5;
        let minRating = 1;
        
        const labelToCheck = question.fullLabel || question.text;
        const scaleMatch = labelToCheck.match(/scale:\s*(?:numerical|numeric)?\s*\((\d+)\)/i) ||
                          labelToCheck.match(/\((\d+)\)/);
        if (scaleMatch) {
          maxRating = parseInt(scaleMatch[1], 10);
        }
        
        const numericResponses = Object.keys(responseCounts)
          .map((v) => parseFloat(v))
          .filter((v) => !isNaN(v));
        if (numericResponses.length > 0) {
          const maxResponse = Math.max(...numericResponses);
          maxRating = Math.max(maxRating, Math.ceil(maxResponse));
        }

        for (let rating = maxRating; rating >= minRating; rating--) {
          const ratingStr = String(rating);
          const count = responseCounts[ratingStr] || 0;
          const percentage = totalAnswers > 0 ? ((count / totalAnswers) * 100).toFixed(1) : '0.0';
          
          answerBreakdown.push({
            value: ratingStr,
            count,
            percentage,
            label: `${rating} star${rating !== 1 ? 's' : ''}`,
          });
        }
      } else {
        answerBreakdown = Object.entries(responseCounts)
          .filter(([_, count]) => count > 0)
          .map(([value, count]) => ({
            value,
            count,
            percentage: totalAnswers > 0 ? ((count / totalAnswers) * 100).toFixed(1) : '0.0',
          }));

        if (question.type === 'textfield') {
          answerBreakdown.sort((a, b) => {
            const aPercent = parseFloat(a.percentage);
            const bPercent = parseFloat(b.percentage);
            if (!isNaN(aPercent) && !isNaN(bPercent)) return bPercent - aPercent;
            return b.count - a.count;
          });
        } else {
          answerBreakdown.sort((a, b) => {
            const aNum = parseFloat(a.value);
            const bNum = parseFloat(b.value);
            if (!isNaN(aNum) && !isNaN(bNum)) return bNum - aNum;
            return b.count - a.count;
          });

          if (question.options && question.options.length > 0 && answerBreakdown.length > 0) {
            answerBreakdown.sort((a, b) => {
              const aIndex = question.options!.indexOf(a.value);
              const bIndex = question.options!.indexOf(b.value);
              if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
              if (aIndex !== -1) return -1;
              if (bIndex !== -1) return 1;
              return b.count - a.count;
            });
          }
        }
      }

      const uniqueUsers = new Set(
        questionResponses
          .filter((r) => r.i_customer !== null && r.i_customer !== undefined)
          .map((r) => r.i_customer)
      ).size;

      const getKeyFn = getKeyFunction(timeGrouping);
      
      const timeSeriesByGroupingAndValue: {
        [key: string]: { [responseValue: string]: number };
      } = {};

      questionResponses.forEach((response) => {
        if (response.created_at && response.user_response !== null && response.user_response !== undefined) {
          try {
            const date = new Date(response.created_at);
            if (!isNaN(date.getTime())) {
              const groupingKey = getKeyFn(date);
              const responseValue = String(response.user_response);
              
              if (!timeSeriesByGroupingAndValue[groupingKey]) {
                timeSeriesByGroupingAndValue[groupingKey] = {};
              }
              timeSeriesByGroupingAndValue[groupingKey][responseValue] = 
                (timeSeriesByGroupingAndValue[groupingKey][responseValue] || 0) + 1;
            }
          } catch (e) {
            console.warn('Invalid date in survey response (timeSeriesByGroupingAndValue):', response.created_at, e);
          }
        }
      });

      const groupingKeys = Object.keys(timeSeriesByGroupingAndValue).sort();
      const allResponseValues = new Set<string>();
      groupingKeys.forEach((groupingKey) => {
        Object.keys(timeSeriesByGroupingAndValue[groupingKey]).forEach((val) => {
          allResponseValues.add(val);
        });
      });

      if (question.type === 'rating') {
        let maxRating = 5;
        const labelToCheck = question.fullLabel || question.text;
        const scaleMatch = labelToCheck.match(/scale:\s*(?:numerical|numeric)?\s*\((\d+)\)/i) ||
                          labelToCheck.match(/\((\d+)\)/);
        if (scaleMatch) {
          maxRating = parseInt(scaleMatch[1], 10);
        }
        for (let rating = 1; rating <= maxRating; rating++) {
          allResponseValues.add(String(rating));
        }
      }

      const timeSeriesArray = groupingKeys.map((groupingKey) => {
        const groupingData: any = { dateKey: groupingKey };
        Array.from(allResponseValues).sort((a, b) => {
          const aNum = parseFloat(a);
          const bNum = parseFloat(b);
          if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
          return a.localeCompare(b);
        }).forEach((val) => {
          groupingData[val] = timeSeriesByGroupingAndValue[groupingKey][val] || 0;
        });
        return groupingData;
      });

      const getQuestionDefaultViewRange = () => {
        if (timeSeriesArray.length === 0) return { start: 0, end: 0 };
        const totalItems = timeSeriesArray.length;
        let itemsToShow: number;
        switch (timeGrouping) {
          case 'day': itemsToShow = Math.min(30, totalItems); break;
          case 'week': itemsToShow = Math.min(12, totalItems); break;
          case 'month': itemsToShow = Math.min(12, totalItems); break;
          case 'year': itemsToShow = totalItems; break;
          default: itemsToShow = Math.min(12, totalItems);
        }
        return { start: Math.max(0, totalItems - itemsToShow), end: totalItems };
      };

      const questionDefaultViewRange = getQuestionDefaultViewRange();

      const timeSeriesData: { [date: string]: number } = {};
      questionResponses.forEach((response) => {
        if (response.created_at) {
          try {
            const date = new Date(response.created_at);
            if (!isNaN(date.getTime())) {
              const key = getKeyFn(date);
              timeSeriesData[key] = (timeSeriesData[key] || 0) + 1;
            }
          } catch (e) {
            console.warn('Invalid date in survey response (timeSeriesArraySimple):', response.created_at, e);
          }
        }
      });

      const timeSeriesArraySimple = Object.entries(timeSeriesData)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));

      const sortedResponseValues = Array.from(allResponseValues).sort((a, b) => {
        const aNum = parseFloat(a);
        const bNum = parseFloat(b);
        if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
        return a.localeCompare(b);
      });
      
      const colors = ['#1976d2', '#2e7d32', '#ed6c02', '#d32f2f', '#9c27b0', '#0288d1', '#388e3c', '#f57c00', '#7b1fa2', '#c2185b'];
      
      const colorMap: { [key: string]: string } = {};
      sortedResponseValues.forEach((val, idx) => {
        colorMap[val] = colors[idx % colors.length];
      });
      
      const answerBreakdownMap = new Map(answerBreakdown.map(a => [a.value, a]));
      const sortedAnswerBreakdown = sortedResponseValues
        .map(val => answerBreakdownMap.get(val))
        .filter(Boolean) as typeof answerBreakdown;

      const questionStat = {
        question,
        totalAnswers,
        uniqueUsers: uniqueUsers > 0 ? uniqueUsers : totalAnswers,
        answerRate: responses.length > 0 ? ((totalAnswers / responses.length) * 100).toFixed(1) : '0',
        answerBreakdown: sortedAnswerBreakdown.length > 0 ? sortedAnswerBreakdown : answerBreakdown,
        timeSeriesData: timeSeriesArraySimple,
        timeSeriesByDayAndValue: timeSeriesArray,
        timeSeriesDefaultView: questionDefaultViewRange,
        responseValues: sortedResponseValues,
        colorMap,
      };

      stats.questionStats.push(questionStat);
    });

    return stats;
  }, [responses, questions, timeGrouping, formatChartDate]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!loading && statistics) {
      if (overallChartRef.current && statistics.overallTimeSeries && statistics.overallTimeSeries.length > 12) {
        const timer = setTimeout(() => {
          if (overallChartRef.current) {
            overallChartRef.current.scrollLeft = overallChartRef.current.scrollWidth;
          }
        }, 300);
        return () => clearTimeout(timer);
      }

      if (statistics.questionStats) {
        statistics.questionStats.forEach((questionStat: any, idx: number) => {
          const key = `${questionStat.question.id || idx}`;
          const chartEl = questionChartRefs.current[key];
          if (chartEl && questionStat.timeSeriesByDayAndValue && questionStat.timeSeriesByDayAndValue.length > 12) {
            setTimeout(() => {
              if (questionChartRefs.current[key]) {
                questionChartRefs.current[key]!.scrollLeft = questionChartRefs.current[key]!.scrollWidth;
              }
            }, 300 + (idx * 50));
          }
        });
      }
    }
  }, [loading, statistics]);

  const formatDate = (date: Date | null): string => {
    if (!date) return '-';
    return formatDateUtil(date.toISOString());
  };

  const handlePreviewOpen = (type: 'excel' | 'pdf') => {
    if (!survey || !statistics) return;
    setPreviewType(type);
    if (type === 'pdf') {
      generatePDFPreview();
    } else if (type === 'excel') {
      generateExcelPreview();
    }
    setPreviewDialogOpen(true);
  };

  const handlePreviewClose = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPreviewDialogOpen(false);
    setPreviewType(null);
  };

  const handleExportConfirm = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      if (previewType === 'excel') {
        await exportToExcel();
      } else if (previewType === 'pdf') {
        await exportToPDF();
      }
      handlePreviewClose();
    } catch {
      toast.error('Could not export the report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const generatePDFDocument = async (): Promise<jsPDF | null> => {
    if (!survey || !statistics) return null;
    
    const originalGrouping = timeGrouping;
    const recommendedGrouping = statistics.timeGrouping || 'day';
    if (timeGrouping !== recommendedGrouping) {
      setTimeGrouping(recommendedGrouping);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    const accordions = document.querySelectorAll('[data-slot="accordion-item"]');
    accordions.forEach((accordion: any) => {
      const trigger = accordion.querySelector('[data-slot="accordion-trigger"]');
      if (trigger && !accordion.querySelector('[data-slot="accordion-content"][data-open]')) {
        trigger.click();
      }
    });
    
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;
    const margin = 20;
    const lineHeight = 7;
    
    const companyBlue = [49, 79, 185];
    
    const addHeader = () => {
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(companyBlue[0], companyBlue[1], companyBlue[2]);
      doc.text('Strata', margin, 15);
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, 25, pageWidth - margin, 25);
    };
    
    const checkNewPage = (requiredSpace: number) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        doc.addPage();
        addHeader();
        yPosition = 35;
        return true;
      }
      return false;
    };
    
    addHeader();
    yPosition = 35;
    
    doc.setFillColor(companyBlue[0], companyBlue[1], companyBlue[2]);
    doc.rect(margin, yPosition, pageWidth - 2 * margin, 40, 'F');
    
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Survey Statistics Report', pageWidth / 2, yPosition + 15, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Comprehensive Analysis & Insights', pageWidth / 2, yPosition + 25, { align: 'center' });
    
    yPosition += 50;
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(companyBlue[0], companyBlue[1], companyBlue[2]);
    doc.text('Strata', margin, yPosition);
    
    yPosition += lineHeight * 2;
    
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(248, 249, 250);
    doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 60, 3, 3, 'FD');
    
    yPosition += 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Survey Information', margin + 10, yPosition);
    yPosition += lineHeight * 1.5;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(`Survey Name: ${survey.name}`, margin + 10, yPosition);
    yPosition += lineHeight * 1.2;
    doc.text(`Survey ID: ${id}`, margin + 10, yPosition);
    yPosition += lineHeight * 1.2;
    if (survey.brand_id) {
      doc.text(`Brand: ${survey.brand_id}`, margin + 10, yPosition);
      yPosition += lineHeight * 1.2;
    }
    if (survey.description) {
      const descLines = doc.splitTextToSize(`Description: ${survey.description}`, pageWidth - 2 * margin - 20);
      doc.text(descLines, margin + 10, yPosition);
      yPosition += descLines.length * lineHeight * 1.2;
    }
    yPosition += 15;
    
    const cardWidth = (pageWidth - 2 * margin - 20) / 2;
    const cardHeight = 35;
    
    doc.setFillColor(225, 245, 254);
    doc.roundedRect(margin, yPosition, cardWidth, cardHeight, 3, 3, 'F');
    doc.setDrawColor(144, 202, 249);
    doc.roundedRect(margin, yPosition, cardWidth, cardHeight, 3, 3, 'D');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Total Responses', margin + 10, yPosition + 8);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(companyBlue[0], companyBlue[1], companyBlue[2]);
    doc.text(statistics.totalResponses.toString(), margin + 10, yPosition + 22);
    
    doc.setFillColor(232, 245, 233);
    doc.roundedRect(margin + cardWidth + 10, yPosition, cardWidth, cardHeight, 3, 3, 'F');
    doc.setDrawColor(129, 199, 132);
    doc.roundedRect(margin + cardWidth + 10, yPosition, cardWidth, cardHeight, 3, 3, 'D');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text('Date Range', margin + cardWidth + 20, yPosition + 8);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    const dateRangeText = statistics.dateRange || 'N/A';
    const dateLines = doc.splitTextToSize(dateRangeText, cardWidth - 20);
    doc.text(dateLines, margin + cardWidth + 20, yPosition + 20);
    
    yPosition += cardHeight + 20;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(150, 150, 150);
    doc.text(`Report Generated: ${formatDateTime(new Date().toISOString())}`, margin, yPosition);
    doc.text('Strata - Professional Survey Analytics', pageWidth - margin, yPosition, { align: 'right' });
    
    yPosition += lineHeight * 2;
    
    doc.addPage();
    addHeader();
    yPosition = 35;
    
    checkNewPage(30);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Overall Statistics', margin, yPosition);
    yPosition += lineHeight * 1.5;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const overallStats = [
      ['Total Responses', statistics.totalResponses.toString()],
      ['First Response', statistics.firstResponse ? formatDate(statistics.firstResponse) : 'N/A'],
      ['Last Response', statistics.lastResponse ? formatDate(statistics.lastResponse) : 'N/A'],
      ['Date Range', statistics.dateRange || 'N/A'],
    ];
    
    autoTable(doc, {
      startY: yPosition,
      head: [['Metric', 'Value']],
      body: overallStats,
      theme: 'striped',
      headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 10 },
      margin: { left: margin, right: margin },
    });
    yPosition = (doc as any).lastAutoTable.finalY + 10;
    
    if (statistics.overallTimeSeries && statistics.overallTimeSeries.length > 0 && overallChartPDFRef.current) {
      checkNewPage(100);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      const groupingLabel = statistics.timeGrouping === 'day' ? 'Daily' : 
                           statistics.timeGrouping === 'week' ? 'Weekly' : 
                           statistics.timeGrouping === 'month' ? 'Monthly' : 'Yearly';
      doc.text(`Response Trends Over Time (${groupingLabel})`, margin, yPosition);
      yPosition += lineHeight * 1.5;
      
      try {
        if (overallChartPDFRef.current) {
          overallChartPDFRef.current.style.visibility = 'visible';
        }
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const canvas = await html2canvas(overallChartPDFRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true,
        });
        
        if (overallChartPDFRef.current) {
          overallChartPDFRef.current.style.visibility = 'hidden';
        }
        const imgData = canvas.toDataURL('image/png');
        
        const maxImgWidth = pageWidth - 2 * margin;
        const imgWidth = Math.min(maxImgWidth, canvas.width / 2);
        const imgHeight = (canvas.height / 2) * (imgWidth / (canvas.width / 2));
        
        let currentY = yPosition;
        
        if (currentY + imgHeight > pageHeight - margin - 20) {
          doc.addPage();
          addHeader();
          currentY = 35;
        }
        
        doc.addImage(imgData, 'PNG', margin, currentY, imgWidth, imgHeight);
        yPosition = currentY + imgHeight + 10;
      } catch (error) {
        console.error('Error capturing overall chart:', error);
        const timeSeriesData = statistics.overallTimeSeries.map((d: any) => [d.date, d.count.toString()]);
        autoTable(doc, {
          startY: yPosition,
          head: [['Date', 'Number of Responses']],
          body: timeSeriesData,
          theme: 'striped',
          headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
          styles: { fontSize: 9 },
          margin: { left: margin, right: margin },
        });
        yPosition = (doc as any).lastAutoTable.finalY + 10;
      }
    }
    
    for (let idx = 0; idx < statistics.questionStats.length; idx++) {
      const questionStat = statistics.questionStats[idx];
      
      if (idx > 0 || yPosition > 50) {
        doc.addPage();
        addHeader();
        yPosition = 35;
      }
      
      const questionNum = questionStat.question.number || idx + 1;
      const questionKey = `${questionStat.question.id || idx}`;
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      const questionTitle = `Question ${questionNum}: ${questionStat.question.text}`;
      const questionTitleLines = doc.splitTextToSize(questionTitle, pageWidth - 2 * margin);
      doc.text(questionTitleLines, margin, yPosition);
      yPosition += questionTitleLines.length * lineHeight + lineHeight;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Type: ${questionStat.question.type} | Total Answers: ${questionStat.totalAnswers} | Unique Users: ${questionStat.uniqueUsers}`, margin, yPosition);
      yPosition += lineHeight * 1.5;
      
      if (questionStat.answerBreakdown.length > 0) {
        const responseData = questionStat.answerBreakdown.map((a: any) => [
          a.value.length > 50 ? a.value.substring(0, 50) + '...' : a.value,
          a.count.toString(),
          `${a.percentage}%`,
        ]);
        
        autoTable(doc, {
          startY: yPosition,
          head: [['Response', 'Count', 'Percentage']],
          body: responseData,
          theme: 'striped',
          headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
          styles: { fontSize: 9 },
          columnStyles: { 0: { cellWidth: 100 }, 1: { cellWidth: 30 }, 2: { cellWidth: 30 } },
          margin: { left: margin, right: margin },
        });
        yPosition = (doc as any).lastAutoTable.finalY + 10;
      }
      
      if (questionStat.question.type === 'rating' && questionStat.answerBreakdown.length > 0) {
        const barChartRef = questionBarChartRefs.current[questionKey];
        if (barChartRef && barChartRef.offsetParent !== null) {
          await new Promise(resolve => setTimeout(resolve, 500));
          checkNewPage(80);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text('Rating Distribution', margin, yPosition);
          yPosition += lineHeight * 1.5;
          
          try {
            const canvas = await html2canvas(barChartRef, { backgroundColor: '#ffffff', scale: 2, logging: false, useCORS: true, allowTaint: true });
            const imgData = canvas.toDataURL('image/png');
            const maxImgWidth = pageWidth - 2 * margin;
            const imgWidth = Math.min(maxImgWidth, canvas.width / 2);
            const imgHeight = (canvas.height / 2) * (imgWidth / (canvas.width / 2));
            if (yPosition + imgHeight > pageHeight - margin - 20) { doc.addPage(); addHeader(); yPosition = 35; }
            doc.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 10;
          } catch (error) {
            console.error(`Error capturing bar chart for question ${questionNum}:`, error);
          }
        }
      }
      
      if ((questionStat.question.type === 'radio' || questionStat.question.type === 'checkbox') && questionStat.answerBreakdown.length > 0) {
        const pieChartRef = questionPieChartRefs.current[questionKey];
        if (pieChartRef && pieChartRef.offsetParent !== null) {
          await new Promise(resolve => setTimeout(resolve, 500));
          checkNewPage(150);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text('Response Distribution', margin, yPosition);
          yPosition += lineHeight * 1.5;
          
          try {
            const canvas = await html2canvas(pieChartRef, { backgroundColor: '#ffffff', scale: 2, logging: false, useCORS: true, allowTaint: true });
            const imgData = canvas.toDataURL('image/png');
            const maxImgWidth = pageWidth - 2 * margin;
            const imgWidth = Math.min(maxImgWidth, canvas.width / 2);
            const imgHeight = (canvas.height / 2) * (imgWidth / (canvas.width / 2));
            if (yPosition + imgHeight > pageHeight - margin - 30) { doc.addPage(); addHeader(); yPosition = 35; }
            doc.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 10;
          } catch (error) {
            console.error(`Error capturing pie chart for question ${questionNum}:`, error);
          }
        }
      }
      
      if (questionStat.question.type !== 'textfield' && questionStat.timeSeriesByDayAndValue && questionStat.timeSeriesByDayAndValue.length > 0) {
        const timeSeriesPDFRef = questionChartPDFRefs.current[questionKey];
        if (timeSeriesPDFRef) {
          await new Promise(resolve => setTimeout(resolve, 800));
          checkNewPage(100);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          const groupingLabel = timeGrouping === 'day' ? 'Daily' : timeGrouping === 'week' ? 'Weekly' : timeGrouping === 'month' ? 'Monthly' : 'Yearly';
          doc.text(`Response Distribution Over Time (${groupingLabel})`, margin, yPosition);
          yPosition += lineHeight * 1.5;
          
          try {
            if (timeSeriesPDFRef) timeSeriesPDFRef.style.visibility = 'visible';
            await new Promise(resolve => setTimeout(resolve, 100));
            const canvas = await html2canvas(timeSeriesPDFRef, { backgroundColor: '#ffffff', scale: 2, logging: false, useCORS: true, allowTaint: true });
            if (timeSeriesPDFRef) timeSeriesPDFRef.style.visibility = 'hidden';
            const imgData = canvas.toDataURL('image/png');
            const maxImgWidth = pageWidth - 2 * margin;
            const imgWidth = Math.min(maxImgWidth, canvas.width / 2);
            const imgHeight = (canvas.height / 2) * (imgWidth / (canvas.width / 2));
            if (yPosition + imgHeight > pageHeight - margin - 20) { doc.addPage(); addHeader(); yPosition = 35; }
            doc.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 10;
          } catch (error) {
            console.error(`Error capturing time series chart for question ${questionNum}:`, error);
          }
        }
      }
      
      yPosition += lineHeight;
    }
    
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text(`Page ${i} of ${totalPages} | Generated on ${formatDateTime(new Date().toISOString())}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(companyBlue[0], companyBlue[1], companyBlue[2]);
      doc.text('Strata', margin, pageHeight - 6);
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text('RICA Act | Self RICA Guide', pageWidth - margin, pageHeight - 6, { align: 'right' });
    }
    
    return doc;
  };

  const generatePDFPreview = async () => {
    const doc = await generatePDFDocument();
    if (!doc) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const pdfBlob = doc.output('blob');
    const url = URL.createObjectURL(pdfBlob);
    setPreviewUrl(url);
    setPreviewDialogOpen(true);
    setPreviewType('pdf');
  };

  const generateExcelPreview = () => {
    if (!survey || !statistics) return;
    
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;margin:20px;background-color:#f5f5f5}.container{background:white;padding:30px;border-radius:8px;box-shadow:0 2px 4px rgba(0,0,0,0.1)}h1{color:#314FB9;font-size:24px;margin-bottom:10px;border-bottom:3px solid #314FB9;padding-bottom:10px}h2{color:#314FB9;font-size:18px;margin-top:25px;margin-bottom:15px}.info-section{margin:20px 0}.info-row{display:flex;padding:8px 0;border-bottom:1px solid #e0e0e0}.info-label{font-weight:bold;width:200px;color:#555}.info-value{flex:1;color:#333}.stats-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:15px;margin:20px 0}.stat-card{background:#f8f9fa;padding:15px;border-radius:6px;border-left:4px solid #314FB9}.stat-label{font-size:12px;color:#666;text-transform:uppercase;margin-bottom:5px}.stat-value{font-size:20px;font-weight:bold;color:#314FB9}.note{margin-top:30px;padding:15px;background:#e3f2fd;border-left:4px solid #1976d2;border-radius:4px;font-size:14px;color:#555}</style></head><body><div class="container"><h1>85 Group - Survey Statistics Report</h1><div class="info-section"><h2>Survey Information</h2><div class="info-row"><div class="info-label">Survey Name:</div><div class="info-value">${survey.name}</div></div><div class="info-row"><div class="info-label">Survey ID:</div><div class="info-value">${id}</div></div>${survey.brand_id ? `<div class="info-row"><div class="info-label">Brand:</div><div class="info-value">${survey.brand_id}</div></div>` : ''}${survey.description ? `<div class="info-row"><div class="info-label">Description:</div><div class="info-value">${survey.description}</div></div>` : ''}</div><div class="info-section"><h2>Overall Statistics</h2><div class="stats-grid"><div class="stat-card"><div class="stat-label">Total Responses</div><div class="stat-value">${statistics.totalResponses}</div></div><div class="stat-card"><div class="stat-label">First Response</div><div class="stat-value" style="font-size:14px">${statistics.firstResponse ? formatDate(statistics.firstResponse) : 'N/A'}</div></div><div class="stat-card"><div class="stat-label">Last Response</div><div class="stat-value" style="font-size:14px">${statistics.lastResponse ? formatDate(statistics.lastResponse) : 'N/A'}</div></div><div class="stat-card"><div class="stat-label">Date Range</div><div class="stat-value" style="font-size:14px">${statistics.dateRange || 'N/A'}</div></div></div></div>${statistics.overallTimeSeries?.length > 0 ? `<div class="info-section"><h2>Time Series Data</h2><div class="note"><strong>Note:</strong> The full Excel export includes a "Time Series" sheet with ${statistics.overallTimeSeries.length} days of data.</div></div>` : ''}${statistics.questionStats?.length > 0 ? `<div class="info-section"><h2>Question Statistics</h2><div class="note"><strong>Note:</strong> The full Excel export includes ${statistics.questionStats.length} question sheets with detailed response breakdowns.</div></div>` : ''}<div class="note"><strong>Report Generated:</strong> ${formatDateTime(new Date().toISOString())}<br><strong>Note:</strong> This is a preview. The full Excel file contains multiple sheets with all survey data.</div></div></body></html>`;
    
    const blob = new Blob([html], { type: 'text/html' });
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
  };

  const exportToExcel = async () => {
    if (!survey || !statistics) return;
    
    const sheets: { name: string; rows: (string | number)[][] }[] = [];
    
    const summaryData = [
      ['Strata - Survey Statistics Report'],
      [''],
      ['Survey Information'],
      ['Survey Name:', survey.name],
      ['Survey ID:', id],
      ...(survey.brand_id ? [['Brand:', survey.brand_id]] : []),
      ['Description:', survey.description || 'N/A'],
      [''],
      ['Overall Statistics'],
      ['Total Responses:', statistics.totalResponses],
      ['First Response:', statistics.firstResponse ? formatDate(statistics.firstResponse) : 'N/A'],
      ['Last Response:', statistics.lastResponse ? formatDate(statistics.lastResponse) : 'N/A'],
      ['Date Range:', statistics.dateRange || 'N/A'],
      [''],
      ['Report Generated:', formatDateTime(new Date().toISOString())],
    ];
    
    sheets.push({ name: 'Summary', rows: summaryData });
    
    if (statistics.overallTimeSeries && statistics.overallTimeSeries.length > 0) {
      const timeSeriesData = [
        ['Date', 'Number of Responses'],
        ...statistics.overallTimeSeries.map((d: any) => [d.date, d.count]),
      ];
      sheets.push({ name: 'Time Series', rows: timeSeriesData });
    }
    
    statistics.questionStats.forEach((questionStat: any, idx: number) => {
      const questionNum = questionStat.question.number || idx + 1;
      const sheetName = `Q${questionNum}`.substring(0, 31);
      
      const questionData = [
        [`Question ${questionNum}: ${questionStat.question.text}`],
        ['Type:', questionStat.question.type],
        ['Total Answers:', questionStat.totalAnswers],
        ['Unique Users:', questionStat.uniqueUsers],
        ['Answer Rate:', `${questionStat.answerRate}%`],
        [''],
        ['Response Breakdown'],
        ['Response', 'Count', 'Percentage'],
        ...questionStat.answerBreakdown.map((a: any) => [a.value, a.count, `${a.percentage}%`]),
      ];
      
      sheets.push({ name: sheetName, rows: questionData });
    });
    
    const sanitizedName = sanitizeFileName(survey.name, 'survey');
    const fileName = `Survey_${id}_${sanitizedName}_${new Date().toISOString().split('T')[0]}.xlsx`;
    const buffer = await buildSurveyWorkbook(sheets);
    downloadSurveyWorkbook(buffer, fileName);
  };

  const exportToPDF = async () => {
    const doc = await generatePDFDocument();
    if (!doc || !survey) return;
    const sanitizedName = sanitizeFileName(survey.name, 'survey');
    const fileName = `Survey_${id}_${sanitizedName}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  return (
    <div className="p-4">
      <MainCard>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-2xl font-bold">Survey Statistics</h2>
          {survey && statistics && (
            <DropdownMenu>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger render={
                    <DropdownMenuTrigger render={
                      <Button variant="ghost" size="icon">
                        <IconDownload className="size-5" />
                      </Button>
                    } />
                  } />
                  <TooltipContent>Download Report</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => handlePreviewOpen('excel')}>
                  <IconFileSpreadsheet className="mr-2 size-4" />
                  Export to Excel
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => handlePreviewOpen('pdf')}>
                  <IconFileTypePdf className="mr-2 size-4" />
                  Export to PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <Dialog open={previewDialogOpen} onOpenChange={(open) => { if (!open) handlePreviewClose(); }}>
          <DialogContent className="h-[90vh] sm:max-w-4xl">
            <DialogHeader>
              <DialogTitle>
                {previewType === 'excel' ? 'Excel Export Preview' : 'PDF Export Preview'}
              </DialogTitle>
              <span className="text-xs text-muted-foreground">
                Preview - {previewType === 'excel' ? 'First sheet shown' : 'Full document preview'}
              </span>
            </DialogHeader>
            <div className="h-full overflow-hidden p-0">
              {previewUrl ? (
                <div className="relative h-full w-full">
                  {previewType === 'pdf' ? (
                    <iframe src={previewUrl} className="h-full w-full border-none" title="PDF Preview" />
                  ) : (
                    <div className="h-full w-full overflow-auto p-2">
                      <iframe src={previewUrl} className="min-h-[600px] w-full rounded border border-border" title="Excel Preview" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex h-full items-center justify-center">
                  <IconLoader2 className="size-8 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handlePreviewClose}>Cancel</Button>
              <Button onClick={handleExportConfirm} disabled={!previewUrl || exporting}>
                Download {previewType === 'excel' ? 'Excel' : 'PDF'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {survey && (
          <h3 className="mb-1 text-base font-semibold">{survey.name}</h3>
        )}
        {survey?.description && (
          <p className="mb-2 text-sm text-muted-foreground">{survey.description}</p>
        )}
        <p className="mb-4 text-sm text-muted-foreground">Survey ID: {id}</p>

        {error && (
          <Alert variant="destructive" className="mb-3">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="flex justify-center p-8">
            <IconLoader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : !statistics ? (
          <Alert>
            <AlertDescription>No survey responses found for this survey.</AlertDescription>
          </Alert>
        ) : (
          <div>
            {/* Summary Statistics Cards */}
            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
              <Card className="p-4">
                <p className="mb-1 text-sm text-muted-foreground">Total Responses</p>
                <p className="text-base font-semibold">{statistics.totalResponses}</p>
              </Card>
              {statistics.firstResponse && (
                <Card className="p-4">
                  <p className="mb-1 text-sm text-muted-foreground">First Response</p>
                  <p className="text-base font-semibold">{formatDate(statistics.firstResponse)}</p>
                </Card>
              )}
              {statistics.lastResponse && (
                <Card className="p-4">
                  <p className="mb-1 text-sm text-muted-foreground">Last Response</p>
                  <p className="text-base font-semibold">{formatDate(statistics.lastResponse)}</p>
                </Card>
              )}
              {statistics.dateRange && (
                <Card className="p-4">
                  <p className="mb-1 text-sm text-muted-foreground">Date Range</p>
                  <p className="text-base font-semibold">{statistics.dateRange}</p>
                </Card>
              )}
            </div>

            {/* Overall Time Series Chart */}
            {statistics.overallTimeSeries && statistics.overallTimeSeries.length > 1 && (
              <>
                <div className="mb-6 mt-6">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-base font-semibold">Survey Response Trends Over Time</h3>
                    <Select value={timeGrouping} onValueChange={(v) => setTimeGrouping(v as any)}>
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="day">Day</SelectItem>
                        <SelectItem value="week">Week</SelectItem>
                        <SelectItem value="month">Month</SelectItem>
                        <SelectItem value="year">Year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <MainCard>
                    <div
                      ref={overallChartRef}
                      className="h-[350px] w-full overflow-x-auto overflow-y-hidden"
                    >
                      <div style={{ minWidth: Math.max(800, statistics.overallTimeSeries.length * 50) }}>
                        <ChartContainer
                          config={{ count: { label: `${timeGrouping === 'day' ? 'Daily' : timeGrouping.charAt(0).toUpperCase() + timeGrouping.slice(1) + 'ly'} Responses`, color: '#1976d2' } } satisfies ShadcnChartConfig}
                          className="h-[350px] w-full"
                        >
                          <AreaChart
                            data={statistics.overallTimeSeries.map((d: any) => ({ time: parseOverallTimeMs(d, timeGrouping), count: d.count }))}
                            margin={{ top: 20, right: 20, bottom: 60, left: 60 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="time" type="number" domain={['dataMin', 'dataMax']} tickFormatter={(ts) => formatChartDate(new Date(ts), timeGrouping)} label={{ value: timeGrouping === 'day' ? 'Date' : timeGrouping === 'week' ? 'Week' : timeGrouping === 'month' ? 'Month' : 'Year', position: 'insideBottom', offset: -12 }} />
                            <YAxis label={{ value: 'Number of Responses', angle: -90, position: 'insideLeft' }} />
                            <ChartTooltip content={<ChartTooltipContent />} />
                            <Area type="monotone" dataKey="count" stroke="var(--color-count)" fill="var(--color-count)" fillOpacity={0.25} />
                          </AreaChart>
                        </ChartContainer>
                      </div>
                    </div>
                    {statistics.overallTimeSeries.length > 12 && (
                      <span className="mt-1 block text-center text-xs text-muted-foreground">
                        Scroll horizontally to view all {timeGrouping}s.
                      </span>
                    )}
                  </MainCard>
                </div>

                {/* Non-scrollable version for PDF (hidden) */}
                <div
                  ref={overallChartPDFRef}
                  className="pointer-events-none invisible absolute -left-[9999px] -top-[9999px] -z-10 overflow-visible"
                  style={{ width: Math.max(800, statistics.overallTimeSeries.length * 50), height: 350 }}
                >
                  <ChartContainer
                    config={{ count: { label: `${timeGrouping.charAt(0).toUpperCase() + timeGrouping.slice(1)}ly Responses`, color: '#1976d2' } } satisfies ShadcnChartConfig}
                    className="h-[350px] w-full"
                    style={{ width: Math.max(800, statistics.overallTimeSeries.length * 50) }}
                  >
                    <AreaChart data={statistics.overallTimeSeries.map((d: any) => ({ time: parseOverallTimeMs(d, timeGrouping), count: d.count }))} margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="time" type="number" domain={['dataMin', 'dataMax']} tickFormatter={(ts) => formatChartDate(new Date(ts), timeGrouping)} label={{ value: timeGrouping === 'day' ? 'Date' : timeGrouping === 'week' ? 'Week' : timeGrouping === 'month' ? 'Month' : 'Year', position: 'insideBottom', offset: -12 }} />
                      <YAxis label={{ value: 'Number of Responses', angle: -90, position: 'insideLeft' }} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Area type="monotone" dataKey="count" stroke="var(--color-count)" fill="var(--color-count)" fillOpacity={0.25} />
                    </AreaChart>
                  </ChartContainer>
                </div>
              </>
            )}

            {/* Question-by-Question Statistics */}
            {statistics.questionStats && statistics.questionStats.length > 0 && (
              <div className="mt-6">
                <h3 className="mb-3 text-base font-semibold">Question-by-Question Statistics</h3>
                <Accordion>
                  {statistics.questionStats.map((questionStat: any, idx: number) => (
                    <AccordionItem key={questionStat.question.id || idx} value={String(questionStat.question.id || idx)} className="mb-1.5">
                      <AccordionTrigger>
                        <div className="flex w-full items-center pr-3">
                          <span className="flex-1 text-left text-sm font-bold">
                            Q{questionStat.question.number}: {questionStat.question.text}
                          </span>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground">{questionStat.question.type.toUpperCase()}</span>
                            <span className="text-xs font-medium text-primary">
                              {questionStat.uniqueUsers} user{questionStat.uniqueUsers !== 1 ? 's' : ''} | {questionStat.totalAnswers} response{questionStat.totalAnswers !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="mb-3">
                          <p className="text-sm text-muted-foreground">
                            Total Responses: {questionStat.totalAnswers} | Unique Users: {questionStat.uniqueUsers}
                          </p>
                        </div>

                        {questionStat.question.options && questionStat.question.options.length > 0 && (
                          <div className="mb-3">
                            <span className="mb-0.5 block text-xs text-muted-foreground">Available Options:</span>
                            <p className="text-sm text-muted-foreground">{questionStat.question.options.join(', ')}</p>
                          </div>
                        )}

                        {/* Charts Section */}
                        {(() => {
                          const hasRatingChart = questionStat.question.type === 'rating' && questionStat.answerBreakdown.length > 0;
                          const hasPieChart = (questionStat.question.type === 'radio' || questionStat.question.type === 'checkbox') && questionStat.answerBreakdown.length > 0;
                          const hasTimeSeriesChart = questionStat.question.type !== 'textfield' && questionStat.timeSeriesByDayAndValue && questionStat.timeSeriesByDayAndValue.length > 0 && questionStat.responseValues && questionStat.responseValues.length > 0;
                          
                          if (!hasRatingChart && !hasPieChart && !hasTimeSeriesChart) return null;
                          
                          return (
                            <div className="mb-4 mt-3">
                              <h4 className="mb-3 text-sm font-semibold">Visualizations:</h4>
                              {/* Bar Chart for Rating Questions */}
                              {hasRatingChart && (
                                <div className="mb-4">
                                  <span className="mb-1 block text-xs text-muted-foreground">Rating Distribution</span>
                                  <div ref={(el) => { questionBarChartRefs.current[`${questionStat.question.id || idx}`] = el; }} className="h-[300px] w-full">
                                    <ChartContainer config={{ users: { label: 'Users', color: '#1976d2' } } satisfies ShadcnChartConfig} className="h-[300px] w-full">
                                      <BarChart data={questionStat.answerBreakdown.map((a: any) => ({ rating: a.label || a.value, users: a.count }))} margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="rating" label={{ value: 'Rating', position: 'insideBottom', offset: -8 }} />
                                        <YAxis label={{ value: 'Number of Users', angle: -90, position: 'insideLeft' }} />
                                        <ChartTooltip content={<ChartTooltipContent />} />
                                        <Bar dataKey="users" fill="var(--color-users)" radius={[4, 4, 0, 0]} />
                                      </BarChart>
                                    </ChartContainer>
                                  </div>
                                </div>
                              )}
                              {/* Pie Chart for Multiple Choice Questions */}
                              {hasPieChart && (
                                <div className="mb-4">
                                  <span className="mb-1 block text-xs text-muted-foreground">Response Distribution</span>
                                  <div ref={(el) => { questionPieChartRefs.current[`${questionStat.question.id || idx}`] = el; }} className="flex h-[480px] w-full items-start justify-center">
                                    <ChartContainer
                                      config={questionStat.answerBreakdown.reduce((acc: ShadcnChartConfig, a: any) => { const label = a.value.length > 30 ? `${a.value.substring(0, 30)}...` : a.value; acc[label] = { label, color: questionStat.colorMap?.[a.value] || '#1976d2' }; return acc; }, {} as ShadcnChartConfig)}
                                      className="mx-auto h-[400px] w-full max-w-[600px]"
                                    >
                                      <PieChart margin={{ top: 56, bottom: 24, left: 20, right: 20 }}>
                                        <ChartLegend content={<ChartLegendContent />} verticalAlign="top" />
                                        <ChartTooltip content={<ChartTooltipContent formatter={(value) => (<span className="font-mono tabular-nums">{typeof value === 'number' ? `${value} (${((value / questionStat.totalAnswers) * 100).toFixed(1)}%)` : String(value)}</span>)} />} />
                                        <Pie data={questionStat.answerBreakdown.map((a: any) => ({ name: a.value.length > 30 ? `${a.value.substring(0, 30)}...` : a.value, value: a.count }))} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={0} outerRadius={120} label>
                                          {questionStat.answerBreakdown.map((a: any, index: number) => (
                                            <Cell key={`cell-${index}`} fill={questionStat.colorMap?.[a.value] || '#1976d2'} />
                                          ))}
                                        </Pie>
                                      </PieChart>
                                    </ChartContainer>
                                  </div>
                                </div>
                              )}
                              {/* Stacked Bar Chart for Response Values Over Time */}
                              {hasTimeSeriesChart && (
                                <div className="mb-4">
                                  <span className="mb-1 block text-xs text-muted-foreground">
                                    Response Distribution Over Time ({timeGrouping === 'day' ? 'Daily' : timeGrouping === 'week' ? 'Weekly' : timeGrouping === 'month' ? 'Monthly' : 'Yearly'})
                                  </span>
                                  <div ref={(el) => { questionChartRefs.current[`${questionStat.question.id || idx}`] = el; }} className="h-[400px] w-full overflow-x-auto overflow-y-hidden">
                                    <div style={{ minWidth: Math.max(800, questionStat.timeSeriesByDayAndValue.length * 50) }}>
                                      {(() => {
                                        const stackedChartData = questionStat.timeSeriesByDayAndValue.map((d: any) => { const row: Record<string, string | number> = { dateLabel: formatTimeSeriesDateLabel(d, timeGrouping, formatChartDate) }; questionStat.responseValues.forEach((val: string) => { row[val] = d[val] || 0; }); return row; });
                                        const stackedConfig: ShadcnChartConfig = questionStat.responseValues.reduce((acc: ShadcnChartConfig, val: string) => { let label = val; if (questionStat.question.type === 'rating') label = `${val} star${parseFloat(val) !== 1 ? 's' : ''}`; acc[val] = { label, color: questionStat.colorMap?.[val] || '#1976d2' }; return acc; }, {});
                                        const xAxisLabel = timeGrouping === 'day' ? 'Date' : timeGrouping === 'week' ? 'Week' : timeGrouping === 'month' ? 'Month' : 'Year';
                                        return (
                                          <ChartContainer config={stackedConfig} className="h-[400px] w-full">
                                            <BarChart data={stackedChartData} margin={{ top: 20, right: 20, bottom: 80, left: 60 }}>
                                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                              <XAxis dataKey="dateLabel" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" height={72} interval={0} label={{ value: xAxisLabel, position: 'insideBottom', offset: -16 }} />
                                              <YAxis label={{ value: 'Number of Responses', angle: -90, position: 'insideLeft' }} />
                                              <ChartTooltip content={<ChartTooltipContent />} />
                                              {questionStat.responseValues.map((val: string) => (
                                                <Bar key={val} dataKey={val} stackId="total" fill={questionStat.colorMap?.[val] || '#1976d2'} />
                                              ))}
                                            </BarChart>
                                          </ChartContainer>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                  {questionStat.timeSeriesByDayAndValue.length > 12 && (
                                    <span className="mt-1 block text-center text-xs text-muted-foreground">
                                      Scroll horizontally to view all dates.
                                    </span>
                                  )}
                                  {/* Non-scrollable version for PDF (hidden) */}
                                  <div
                                    ref={(el) => { questionChartPDFRefs.current[`${questionStat.question.id || idx}`] = el; }}
                                    className="pointer-events-none invisible absolute -left-[9999px] -top-[9999px] -z-10 overflow-visible"
                                    style={{ width: Math.max(800, questionStat.timeSeriesByDayAndValue.length * 50), height: 400 }}
                                  >
                                    {(() => {
                                      const stackedChartData = questionStat.timeSeriesByDayAndValue.map((d: any) => { const row: Record<string, string | number> = { dateLabel: formatTimeSeriesDateLabel(d, timeGrouping, formatChartDate) }; questionStat.responseValues.forEach((val: string) => { row[val] = d[val] || 0; }); return row; });
                                      const stackedConfig: ShadcnChartConfig = questionStat.responseValues.reduce((acc: ShadcnChartConfig, val: string) => { let label = val; if (questionStat.question.type === 'rating') label = `${val} star${parseFloat(val) !== 1 ? 's' : ''}`; acc[val] = { label, color: questionStat.colorMap?.[val] || '#1976d2' }; return acc; }, {});
                                      const xAxisLabel = timeGrouping === 'day' ? 'Date' : timeGrouping === 'week' ? 'Week' : timeGrouping === 'month' ? 'Month' : 'Year';
                                      return (
                                        <ChartContainer config={stackedConfig} className="h-[400px] w-full" style={{ width: Math.max(800, questionStat.timeSeriesByDayAndValue.length * 50) }}>
                                          <BarChart data={stackedChartData} margin={{ top: 20, right: 20, bottom: 80, left: 60 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="dateLabel" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" height={72} interval={0} label={{ value: xAxisLabel, position: 'insideBottom', offset: -16 }} />
                                            <YAxis label={{ value: 'Number of Responses', angle: -90, position: 'insideLeft' }} />
                                            <ChartTooltip content={<ChartTooltipContent />} />
                                            {questionStat.responseValues.map((val: string) => (
                                              <Bar key={val} dataKey={val} stackId="total" fill={questionStat.colorMap?.[val] || '#1976d2'} />
                                            ))}
                                          </BarChart>
                                        </ChartContainer>
                                      );
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {questionStat.answerBreakdown.length > 0 ? (
                          <div className="mt-3">
                            <div className="mb-1.5 flex items-center justify-between">
                              <h4 className="text-sm font-semibold">Response Statistics:</h4>
                              {questionStat.question.type === 'textfield' && questionStat.answerBreakdown.length > 10 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const questionKey = `${questionStat.question.id || idx}`;
                                    setExpandedTextfieldQuestions(prev => {
                                      const newSet = new Set(prev);
                                      if (newSet.has(questionKey)) newSet.delete(questionKey);
                                      else newSet.add(questionKey);
                                      return newSet;
                                    });
                                  }}
                                >
                                  {expandedTextfieldQuestions.has(`${questionStat.question.id || idx}`) ? 'Show Top 10' : 'View All'}
                                </Button>
                              )}
                            </div>
                            {(() => {
                              const questionKey = `${questionStat.question.id || idx}`;
                              const isExpanded = expandedTextfieldQuestions.has(questionKey);
                              const displayBreakdown = questionStat.question.type === 'textfield' && !isExpanded
                                ? questionStat.answerBreakdown.slice(0, 10)
                                : questionStat.answerBreakdown;
                              
                              return displayBreakdown.map((answer: any, answerIdx: number) => {
                                const displayLabel = answer.label || answer.value;
                                const userText = answer.count === 1 ? 'user' : 'users';
                                
                                return (
                                  <div
                                    key={answerIdx}
                                    className={cn(
                                      'flex items-center justify-between rounded px-2 py-2',
                                      answerIdx < displayBreakdown.length - 1 && 'mb-0.5 border-b border-border',
                                      answer.count === 0 && 'bg-muted/50'
                                    )}
                                  >
                                    <div className="flex flex-1 items-center gap-1.5">
                                      <span className={cn('text-sm', answer.count > 0 ? 'font-medium' : '')}>{displayLabel}</span>
                                      {answer.count === 0 && (
                                        <span className="text-xs italic text-muted-foreground">(no responses)</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className={cn('text-sm font-bold', answer.count > 0 ? 'text-primary' : 'text-muted-foreground')}>
                                        {answer.count} {userText}
                                      </span>
                                      <span className={cn('text-sm font-bold', answer.count > 0 ? 'text-primary' : 'text-muted-foreground')}>
                                        {answer.percentage}%
                                      </span>
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        ) : (
                          <p className="mt-1 text-sm text-muted-foreground">No responses found for this question</p>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            {/* Fallback: Show raw field breakdown if no questions found */}
            {(!statistics.questionStats || statistics.questionStats.length === 0) && responses.length > 0 && (
              <div className="mt-6">
                <Alert className="mb-3">
                  <AlertDescription>Survey questions not found. Showing raw response data.</AlertDescription>
                </Alert>
                <h3 className="mb-3 text-base font-semibold">Response Breakdown by Field</h3>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {Object.keys(responses[0] || {})
                    .filter((key) => !['id', 'survey_id', 'created_at', 'updated_at', 'submitted_at', 'date'].includes(key) && !key.includes('_at'))
                    .slice(0, 10)
                    .map((field) => {
                      const fieldStats: { [value: string]: number } = {};
                      responses.forEach((response) => {
                        const value = response[field];
                        if (value !== null && value !== undefined) {
                          const valueStr = String(value);
                          fieldStats[valueStr] = (fieldStats[valueStr] || 0) + 1;
                        }
                      });
                      const values = Object.entries(fieldStats)
                        .map(([value, count]) => ({ value, count }))
                        .sort((a, b) => b.count - a.count)
                        .slice(0, 5);
                      
                      return (
                        <Card key={field} className="p-4">
                          <h4 className="mb-2 text-sm font-bold">
                            {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </h4>
                          <div>
                            {values.map((item, idx) => (
                              <div
                                key={idx}
                                className={cn(
                                  'flex items-center justify-between py-1',
                                  idx < values.length - 1 && 'border-b border-border'
                                )}
                              >
                                <span className="text-sm">
                                  {item.value.length > 50 ? `${item.value.substring(0, 50)}...` : item.value}
                                </span>
                                <span className="text-sm font-bold text-primary">
                                  {item.count} ({statistics?.totalResponses ? ((item.count / statistics.totalResponses) * 100).toFixed(1) : '0'}%)
                                </span>
                              </div>
                            ))}
                          </div>
                        </Card>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        )}
      </MainCard>
    </div>
  );
}
